<template>
  <!-- Feature 42 — Device Playback Handoff.
       A small global pill that surfaces playback happening on ANOTHER
       device on the same invite ("▶ Playing on the couch — <title>").
       Renders nothing at all when there's nothing to show — the v-if
       guards mean zero DOM in the empty/steady state. -->
  <div
    v-if="visibleSession"
    class="handoff-banner"
    :class="{ 'handoff-banner--tv': uiStore.tvMode }"
    role="status"
    aria-live="polite"
  >
    <img
      v-if="visibleSession.poster"
      :src="visibleSession.poster"
      :alt="visibleSession.title"
      loading="lazy"
      class="handoff-banner__poster"
    />
    <span class="handoff-banner__text">
      <span aria-hidden="true">▶ </span>Playing on
      <strong>{{ visibleSession.deviceName || 'another device' }}</strong>
      — {{ visibleSession.title }}
    </span>
    <button
      type="button"
      class="handoff-banner__continue"
      @click="continueHere"
    >
      Continue here →
    </button>
    <button
      type="button"
      class="handoff-banner__dismiss"
      aria-label="Dismiss"
      @click="dismiss(visibleSession)"
    >✕</button>
  </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useHandoffStore, isLocalRoute } from '@/stores/handoff'
import { useUiStore } from '@/stores/ui'

const route = useRoute()
const router = useRouter()
const uiStore = useUiStore()
// Shared with HandoffIndicator (the header icon): one poll, one dismissed set.
const handoff = useHandoffStore()

// First active foreign session whose target route (path only, query stripped)
// differs from where the user already is — so a user already on that title's
// player isn't nagged to "continue" into the page they're on.
const visibleSession = computed(
  () =>
    handoff.activeSessions.find((s) => s.route.split('?')[0] !== route.path) ||
    null,
)

function continueHere() {
  const s = visibleSession.value
  if (!s || !isLocalRoute(s.route)) return
  const sep = s.route.includes('?') ? '&' : '?'
  router.push(s.route + sep + 'handoff=1')
}

function dismiss(s) {
  handoff.dismiss(s)
}

onMounted(() => handoff.startPolling())
onBeforeUnmount(() => handoff.stopPolling())
</script>

<style scoped>
.handoff-banner {
  position: sticky;
  top: 0.5rem;
  z-index: 30;
  margin: 0.5rem auto 0;
  max-width: 38rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.45rem 0.6rem 0.45rem 0.55rem;
  border-radius: 9999px;
  background: rgb(17 24 39 / 0.92);
  color: #f9fafb;
  box-shadow: 0 8px 22px -10px rgb(0 0 0 / 0.6);
  border: 1px solid rgb(255 255 255 / 0.08);
  backdrop-filter: blur(6px);
  font-size: 0.85rem;
  line-height: 1.3;
}

.handoff-banner__poster {
  flex-shrink: 0;
  width: 1.75rem;
  height: 2.6rem;
  object-fit: cover;
  border-radius: 0.35rem;
}

.handoff-banner__text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.handoff-banner__text strong { font-weight: 600; }

.handoff-banner__continue {
  flex-shrink: 0;
  padding: 0.3rem 0.7rem;
  border-radius: 9999px;
  background: rgb(37 99 235);
  color: #fff;
  font-weight: 600;
  font-size: 0.8em;
  transition: background-color 150ms ease;
}
.handoff-banner__continue:hover { background: rgb(29 78 216); }

.handoff-banner__dismiss {
  flex-shrink: 0;
  opacity: 0.6;
  padding: 0.2rem 0.35rem;
  color: inherit;
  font-size: 0.95em;
  line-height: 1;
}
.handoff-banner__dismiss:hover { opacity: 1; }

/* TV-mode parity: larger text/padding so it reads across the room, and a
   visible focus ring on the actionable button using the shared
   density focus-ring token. */
.handoff-banner--tv {
  max-width: 52rem;
  gap: 1rem;
  padding: 0.8rem 1rem;
  font-size: 1.15rem;
  top: 1rem;
}
.handoff-banner--tv .handoff-banner__poster {
  width: 2.4rem;
  height: 3.6rem;
}
.handoff-banner--tv .handoff-banner__continue {
  padding: 0.55rem 1.1rem;
}
.handoff-banner--tv .handoff-banner__continue:focus-visible,
.handoff-banner--tv .handoff-banner__dismiss:focus-visible {
  outline: 3px solid var(--density-focus-ring-color, #fff);
  outline-offset: 2px;
}
</style>
